import React, { useRef, useEffect, useState } from "react";
import { Link, useNavigate, Navigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getUser, userLogin } from "features/auth/userSlice";
import Header from "components/header/Header";
import useAuth from "components/common/hooks/useAuth";
import Footer from "components/footer/Footer";
import { fetchClients } from "features/jobinv/JobCardSlice";
import { getErrorMessage } from "utils/ErrorMessage";
import styled from "styled-components";

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  // background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
`;

const LoginCard = styled.div`
  background: white;
  border-radius: 24px;
  padding: 48px;
  width: 100%;
  max-width: 440px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  
  @media (max-width: 640px) {
    padding: 32px 24px;
  }
`;

const Logo = styled.div`
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  font-size: 28px;
  font-weight: 700;
  color: white;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  font-size: 15px;
  color: #6b7280;
  margin: 0 0 32px 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 14px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 15px;
  transition: all 0.2s ease;
  outline: none;
  
  &:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 14px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:before {
    content: "⚠";
    font-size: 16px;
  }
`;

const Button = styled.button`
  background:#00afe2;
  color: white;
  border: none;
  padding: 14px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 8px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ForgotPassword = styled.div`
  text-align: center;
  margin-top: 24px;
  
  a {
    color: #667eea;
    text-decoration: none;
    font-size: 14px;
    font-weight: 500;
    transition: color 0.2s ease;
    
    &:hover {
      color: #764ba2;
    }
  }
`;

const LoaderDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  
  span {
    width: 8px;
    height: 8px;
    background: white;
    border-radius: 50%;
    animation: bounce 1.4s infinite ease-in-out both;
    
    &:nth-child(1) {
      animation-delay: -0.32s;
    }
    
    &:nth-child(2) {
      animation-delay: -0.16s;
    }
  }
  
  @keyframes bounce {
    0%, 80%, 100% {
      transform: scale(0);
    }
    40% {
      transform: scale(1);
    }
  }
`;

const Login = () => {
  const { setAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";
  const userRef = useRef();
  const dispatch = useDispatch();
  const [loginError, setLoginError] = useState(false);
  const { loading, error, user, userToken } = useSelector(
    (state) => state.user
  );

  useEffect(() => {
    if (!error) return;
    const message = getErrorMessage(error);
    setLoginError(message);
  }, [error]);

  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });

  const { username, password } = formData;

  const onChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setLoginError(false);
  };

  if (user && userToken) {
    // Redirect based on role after successful login
    if (user.roll === "admin" || user.roll === "viewer") {
      // Admin / viewer → main dashboard
      return <Navigate to="/" />;
    }

    if (user.roll === "store") {
      // Store user → cloth inventory (no access to jobcards by default)
      return <Navigate to="/inventory" />;
    }

    if (user.roll === "degitizer") {
      // Digitizer → digitizer jobcards page only
      return <Navigate to="/digitizer-jobcards" />;
    }

    if (user.roll === "sampling_digitizer") {
      // Sampling Digitizer → sampling digitizer jobcards page only
      return <Navigate to="/sampling-digitizer-jobcards" />;
    }

    // All other roles → jobcard page
    return <Navigate to="/jobcard" />;
  }

  const onSubmit = (e) => {
    e.preventDefault();
    dispatch(userLogin({ username, password }));
  };

  return (
    <>
      <LoginContainer>
        <LoginCard>
          {/* <Logo>VA</Logo> */}
          <Title>Welcome back</Title>
          <Subtitle>Enter your credentials to access your account</Subtitle>

          {loginError && <ErrorMessage>{loginError}</ErrorMessage>}

          <Form onSubmit={onSubmit}>
            <InputGroup>
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                name="username"
                placeholder="Enter your username"
                value={username}
                onChange={onChange}
                required
                autoComplete="username"
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                name="password"
                placeholder="Enter your password"
                value={password}
                onChange={onChange}
                required
                autoComplete="current-password"
              />
            </InputGroup>

            <Button type="submit" disabled={loading}>
              {loading ? (
                <LoaderDots>
                  <span></span>
                  <span></span>
                  <span></span>
                </LoaderDots>
              ) : (
                "Sign in"
              )}
            </Button>
          </Form>

          <ForgotPassword>
            <Link to="/forgot-password">Forgot your password?</Link>
          </ForgotPassword>
        </LoginCard>
      </LoginContainer>
      <Footer />
    </>
  );
};

export default Login;